import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Save, Trash2, Edit2, Copy, ChevronDown, ChevronRight,
  ClipboardList, FileText, GripVertical, X, CheckCircle2, AlertCircle,
  RefreshCw, Eye, Settings, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Dropdown (Single)' },
  { value: 'multiselect', label: 'Checkboxes (Multiple)' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Yes/No Checkbox' },
];

const PROJECT_TYPE_MAPPING = {
  'remodel': ['Renovation', 'Whole Home Renovation', 'Addition'],
  'new_build': ['New Construction'],
  'kitchen': ['Kitchen Remodel'],
  'bathroom': ['Bath Remodel', 'Bathroom Remodel'],
  'interior_design': ['Interior Design'],
  'consultation': ['Design Consulting', 'Consultation'],
  'general': [],
};

export default function SurveyTemplatesPage() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Question editing
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    question_type: 'text',
    required: false,
    category: '',
    placeholder: '',
    help_text: '',
    options: [],
  });
  
  // New template dialog
  const [showNewTemplateDialog, setShowNewTemplateDialog] = useState(false);
  const [newTemplateData, setNewTemplateData] = useState({
    name: '',
    description: '',
    project_types: [],
  });
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const checkDarkMode = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    checkDarkMode();
    window.addEventListener('storage', checkDarkMode);
    return () => window.removeEventListener('storage', checkDarkMode);
  }, []);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/survey-templates`);
      setTemplates(response.data);
      
      // Select first template by default
      if (response.data.length > 0 && !selectedTemplate) {
        loadTemplateDetails(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load survey templates');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetails = async (templateId) => {
    try {
      const response = await axios.get(`${API}/api/survey-templates/${templateId}`);
      setSelectedTemplate(response.data);
      setEditingTemplate(JSON.parse(JSON.stringify(response.data))); // Deep clone
      
      // Expand all categories
      const categories = {};
      response.data.questions?.forEach(q => {
        if (q.category) categories[q.category] = true;
      });
      setExpandedCategories(categories);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template details');
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/api/survey-templates/${editingTemplate.id}`, editingTemplate);
      toast.success('Template saved successfully');
      await loadTemplates();
      await loadTemplateDetails(editingTemplate.id);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateData.name) {
      toast.error('Template name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/api/survey-templates`, {
        ...newTemplateData,
        questions: [],
      });
      toast.success('Template created');
      setShowNewTemplateDialog(false);
      setNewTemplateData({ name: '', description: '', project_types: [] });
      await loadTemplates();
      loadTemplateDetails(response.data.id);
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/api/survey-templates/${templateId}`);
      toast.success('Template deleted');
      setSelectedTemplate(null);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const response = await axios.post(`${API}/api/survey-templates`, {
        name: `${template.name} (Copy)`,
        description: template.description,
        questions: template.questions || [],
        project_types: template.project_types || [],
      });
      toast.success('Template duplicated');
      await loadTemplates();
      loadTemplateDetails(response.data.id);
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  // Question management
  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setNewQuestion({
      id: `q_${Date.now()}`,
      question: '',
      question_type: 'text',
      required: false,
      category: editingTemplate?.questions?.[0]?.category || 'General',
      placeholder: '',
      help_text: '',
      options: [],
    });
    setShowQuestionDialog(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setNewQuestion({ ...question, options: question.options || [] });
    setShowQuestionDialog(true);
  };

  const handleSaveQuestion = () => {
    if (!newQuestion.question.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    const updatedQuestions = [...(editingTemplate.questions || [])];
    
    if (editingQuestion) {
      // Update existing question
      const idx = updatedQuestions.findIndex(q => q.id === editingQuestion.id);
      if (idx >= 0) {
        updatedQuestions[idx] = { ...newQuestion };
      }
    } else {
      // Add new question
      updatedQuestions.push({ ...newQuestion });
    }
    
    setEditingTemplate({ ...editingTemplate, questions: updatedQuestions });
    setShowQuestionDialog(false);
    toast.success(editingQuestion ? 'Question updated' : 'Question added');
  };

  const handleDeleteQuestion = (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    
    const updatedQuestions = editingTemplate.questions.filter(q => q.id !== questionId);
    setEditingTemplate({ ...editingTemplate, questions: updatedQuestions });
    toast.success('Question deleted');
  };

  const handleMoveQuestion = (questionId, direction) => {
    const questions = [...editingTemplate.questions];
    const idx = questions.findIndex(q => q.id === questionId);
    if (idx < 0) return;
    
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= questions.length) return;
    
    [questions[idx], questions[newIdx]] = [questions[newIdx], questions[idx]];
    setEditingTemplate({ ...editingTemplate, questions });
  };

  // Group questions by category
  const questionsByCategory = editingTemplate?.questions?.reduce((acc, q) => {
    const cat = q.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(q);
    return acc;
  }, {}) || {};

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const cardClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]';
  const inputClass = darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD]' : '';
  const textClass = darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]';
  const mutedClass = darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
        <RefreshCw className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white'} border-b px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className={darkMode ? 'hover:bg-[#1A1A1A]' : ''}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className={`text-xl font-heading ${textClass}`}>Survey Templates</h1>
                <HelpIcon elementId="survey-templates" position="inline" />
              </div>
              <p className={`text-sm ${mutedClass}`}>Manage client survey questionnaires</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowNewTemplateDialog(true)}
              className={darkMode ? 'border-[#2A2A2A]' : ''}
              data-testid="new-template-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
            {editingTemplate && (
              <Button 
                onClick={handleSaveTemplate}
                disabled={saving}
                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                data-testid="save-template-btn"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar - Template List */}
        <aside className={`w-72 border-r ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'bg-white'} min-h-[calc(100vh-73px)] p-4`}>
          <div className="space-y-2">
            {templates.map(template => (
              <div
                key={template.id}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedTemplate?.id === template.id
                    ? 'bg-[#BC5D41]/10 border border-[#BC5D41]'
                    : darkMode ? 'hover:bg-[#1A1A1A] border border-transparent' : 'hover:bg-[#FAF9F7] border border-transparent'
                }`}
                onClick={() => loadTemplateDetails(template.id)}
                data-testid={`template-item-${template.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium text-sm truncate ${textClass}`}>{template.name}</h3>
                    <p className={`text-xs ${mutedClass} mt-0.5`}>{template.question_count} questions</p>
                  </div>
                  <div className="flex gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={(e) => { e.stopPropagation(); handleDuplicateTemplate(template); }}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Duplicate</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Main Content - Template Editor */}
        <main className="flex-1 p-6">
          {editingTemplate ? (
            <div className="max-w-4xl space-y-6">
              {/* Template Info */}
              <Card className={cardClass}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                    <ClipboardList className="w-5 h-5 text-[#BC5D41]" />
                    Template Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={mutedClass}>Template Name</Label>
                      <Input
                        value={editingTemplate.name}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                        className={inputClass}
                        data-testid="template-name-input"
                      />
                    </div>
                    <div>
                      <Label className={mutedClass}>Linked Project Types</Label>
                      <p className={`text-sm ${mutedClass} mt-2`}>
                        {PROJECT_TYPE_MAPPING[editingTemplate.id]?.join(', ') || 'All project types'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label className={mutedClass}>Description</Label>
                    <Textarea
                      value={editingTemplate.description || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                      className={inputClass}
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDeleteTemplate(editingTemplate.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Template
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Questions Section */}
              <Card className={cardClass}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                      <FileText className="w-5 h-5 text-[#BC5D41]" />
                      Questions ({editingTemplate.questions?.length || 0})
                    </CardTitle>
                    <Button onClick={handleAddQuestion} size="sm" data-testid="add-question-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Question
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {Object.entries(questionsByCategory).length === 0 ? (
                    <div className={`text-center py-8 ${mutedClass}`}>
                      <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No questions yet</p>
                      <p className="text-sm mt-1">Click "Add Question" to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(questionsByCategory).map(([category, questions]) => (
                        <Collapsible 
                          key={category} 
                          open={expandedCategories[category]} 
                          onOpenChange={() => toggleCategory(category)}
                        >
                          <CollapsibleTrigger className={`w-full flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-[#2A2A2A]' : 'bg-[#FAF9F7]'}`}>
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${textClass}`}>{category}</span>
                              <Badge variant="outline">{questions.length}</Badge>
                            </div>
                            {expandedCategories[category] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2 space-y-2">
                            {questions.map((q, idx) => (
                              <div 
                                key={q.id} 
                                className={`p-3 rounded-lg border ${darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-6 text-center text-sm ${mutedClass}`}>{idx + 1}</div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm ${textClass}`}>
                                      {q.question}
                                      {q.required && <span className="text-red-500 ml-1">*</span>}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge variant="outline" className="text-xs">{q.question_type}</Badge>
                                      {q.options?.length > 0 && (
                                        <span className={`text-xs ${mutedClass}`}>{q.options.length} options</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditQuestion(q)}>
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteQuestion(q.id)}>
                                      <Trash2 className="w-3 h-3 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </CollapsibleContent>
                        </Collapsible>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className={`text-center py-12 ${mutedClass}`}>
              <ClipboardList className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Select a template to edit</p>
              <p className="text-sm mt-1">Or create a new one</p>
            </div>
          )}
        </main>
      </div>

      {/* Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className={`max-w-lg ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={textClass}>
              {editingQuestion ? 'Edit Question' : 'Add Question'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={mutedClass}>Question Text *</Label>
              <Textarea
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                className={inputClass}
                rows={2}
                placeholder="Enter your question..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={mutedClass}>Type</Label>
                <Select 
                  value={newQuestion.question_type} 
                  onValueChange={(v) => setNewQuestion({ ...newQuestion, question_type: v })}
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={mutedClass}>Category</Label>
                <Input
                  value={newQuestion.category}
                  onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
                  className={inputClass}
                  placeholder="e.g., Project Information"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={newQuestion.required}
                onCheckedChange={(checked) => setNewQuestion({ ...newQuestion, required: checked })}
              />
              <Label className={mutedClass}>Required question</Label>
            </div>
            <div>
              <Label className={mutedClass}>Placeholder Text</Label>
              <Input
                value={newQuestion.placeholder || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, placeholder: e.target.value })}
                className={inputClass}
                placeholder="Hint text shown in empty field..."
              />
            </div>
            {['select', 'multiselect', 'radio'].includes(newQuestion.question_type) && (
              <div>
                <Label className={mutedClass}>Options (one per line)</Label>
                <Textarea
                  value={(newQuestion.options || []).join('\n')}
                  onChange={(e) => setNewQuestion({ 
                    ...newQuestion, 
                    options: e.target.value.split('\n').filter(o => o.trim()) 
                  })}
                  className={inputClass}
                  rows={4}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestionDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveQuestion} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              {editingQuestion ? 'Update' : 'Add'} Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Template Dialog */}
      <Dialog open={showNewTemplateDialog} onOpenChange={setShowNewTemplateDialog}>
        <DialogContent className={`max-w-md ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={textClass}>Create New Survey Template</DialogTitle>
            <DialogDescription className={mutedClass}>
              Create a blank template or duplicate an existing one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={mutedClass}>Template Name *</Label>
              <Input
                value={newTemplateData.name}
                onChange={(e) => setNewTemplateData({ ...newTemplateData, name: e.target.value })}
                className={inputClass}
                placeholder="e.g., Kitchen Remodel Survey"
              />
            </div>
            <div>
              <Label className={mutedClass}>Description</Label>
              <Textarea
                value={newTemplateData.description}
                onChange={(e) => setNewTemplateData({ ...newTemplateData, description: e.target.value })}
                className={inputClass}
                rows={2}
                placeholder="What types of projects is this survey for?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTemplateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateTemplate} 
              disabled={saving || !newTemplateData.name}
              className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            >
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
